import type React from 'react'
import {useEffect, useRef} from 'react'

import {waitForConsentPreferences} from '@github-ui/cookie-consent'
import {doNotTrack} from '@github-ui/do-not-track'

import type {VendorPixel} from '../../lib/types/analytics/vendor-pixel'
import {firePixelsOnClick} from '../../lib/vendor-pixels/fire-vendor-pixel'
import {usePixelsOnPageview} from '../../lib/vendor-pixels/use-pixels-on-pageview'

export const VENDOR_PIXEL_EVENT_ATTR = 'data-vendor-pixel-event'

interface VendorPixelTrackerProps {
  vendorPixels: VendorPixel[]
  children: React.ReactNode
}

/**
 * Event delegation component that fires vendor tracking pixels when a user
 * clicks an element annotated with `data-vendor-pixel-event` attribute.
 *
 * Place this as an ancestor of any elements that should trigger pixel tracking.
 * The attribute is inert when vendorPixels is empty (path not enabled).
 *
 * Pixels are only fired on the first qualifying click
 * per component mount (i.e., per page visit). Subsequent clicks skip those pixels.
 *
 * Respects user privacy preferences: pixels are not fired when Do Not Track /
 * Global Privacy Control is enabled, or when the user has not consented to
 * Analytics cookies.
 *
 * Also fires "page view" pixels once on mount for vendors that provide a
 * `pageViewUrl`, using the same privacy checks.
 */
export function VendorPixelTracker({vendorPixels, children}: VendorPixelTrackerProps) {
  const ref = useRef<HTMLDivElement>(null)

  // Fire "page view" pixels once on mount (consent- and DNT-gated internally)
  usePixelsOnPageview(vendorPixels)

  // This keeps track of the pixel vendor keys that have fired to make sure it fires only once
  const firedKeysRef = useRef<Set<string>>(new Set())

  useEffect(() => {
    const el = ref.current
    if (!el || vendorPixels.length === 0) return

    if (doNotTrack()) return

    // Guard against attaching listeners after unmount (waitForConsentPreferences is async)
    let unmounted = false
    const firedKeys = firedKeysRef.current

    const handler = (event: MouseEvent) => {
      const target = (event.target as HTMLElement).closest?.(`[${VENDOR_PIXEL_EVENT_ATTR}]`)
      if (!target) return
      firePixelsOnClick(vendorPixels, firedKeys)
    }

    async function attachListener() {
      const consentPreferences = await waitForConsentPreferences()
      if (unmounted || !el) return
      if (consentPreferences && consentPreferences['Analytics']) {
        el.addEventListener('click', handler)
      }
    }

    attachListener()

    return () => {
      unmounted = true
      el.removeEventListener('click', handler)
      // Reset so pixels can fire again on next mount / page visit
      firedKeys.clear()
    }
  }, [vendorPixels])

  return <div ref={ref}>{children}</div>
}
